import { smartToastCriticalError } from '@/utils/smartToast';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import useActionsCapabilitiesStore from '../Components/ActionsCapabilities/store';
import { useCancelRequestStore } from '../store/cancelRequestStore';

// Toast configuration constants
const TOAST_DURATIONS = {
  CRITICAL: 0, // Never auto-dismiss - requires user action
  IMPORTANT: 8000, // 8 seconds
  INFORMATIVE: 7000, // 7 seconds
  WARNING: 6000, // 6 seconds
};

// Helper functions
const validatePrompt = (prompt) => {
  if (!prompt?.trim()) {
    toast.error('Remember: Messages need text too!', {
      duration: TOAST_DURATIONS.IMPORTANT,
    });
    return false;
  }
  return true;
};

const validateRequiredParameters = (
  selectedModelGeneration,
  selectedModelParametersGeneration
) => {
  const requiredParameters = selectedModelGeneration.parameters.filter(
    (param) => param.required && param.property !== 'prompt'
  );

  const missingParameters = requiredParameters.filter(
    (param) =>
      !selectedModelParametersGeneration ||
      selectedModelParametersGeneration[param.property] === undefined ||
      selectedModelParametersGeneration[param.property] === ''
  );

  if (missingParameters.length > 0) {
    smartToastCriticalError(
      `Missing required parameters: ${missingParameters
        .map((param) => param.label)
        .join(', ')}`,
      {
        id: 'missing-parameters',
      }
    );
    return false;
  }
  return true;
};

const validateVideoGeneration = (imagesPreview, selectedModelGeneration) => {
  if (
    (imagesPreview?.length ?? 0) === 0 &&
    selectedModelGeneration.type.toLowerCase() === 'video'
  ) {
    toast.error(
      'Remember to attach an image to serve as input for the image-to-video model',
      {
        duration: TOAST_DURATIONS.IMPORTANT,
      }
    );
    return false;
  }
  return true;
};

const resetState = (setters) => {
  const {
    setLoading,
    setInputValue,
    setImagesPreview,
    setSelectedModelGeneration,
    setSelectedModelParametersGeneration,
    setGenerationsModelsActive,
    setStreamingMessage,
  } = setters;

  setStreamingMessage(null);
  setLoading(false);
  setInputValue('');
  setImagesPreview([]);
  setSelectedModelGeneration(null);
  setSelectedModelParametersGeneration(null);
  setGenerationsModelsActive(false);
};

const createStreamingMessage = (model, modelRef, modelType, numImages = 1) => ({
  model,
  modelRef,
  data: '',
  finished: false,
  imagesURL:
    modelType === 'image'
      ? Array(numImages).fill({ url: 'example.com' })
      : null,
  videoURL: modelType === 'video' ? 'example.com' : null,
  audioURL: modelType === 'audio' ? 'example.com' : null,
});

const useMediaGeneratorServices = ({
  store,
  selectedChat,
  imagesPreview,
  setLoading,
  setInputValue,
  setImagesPreview,
  setStreamingMessage,
  setChat,
  setAutoScroll,
  addMessage,
  getChat,
  onNewChat,
  navigate,
  location,
  parseQueryString,
  visionInput,
  setMessages,
  toastReward,
  isProcessing,
  messages,
}) => {
  const {
    setGenerationsModelsActive,
    selectedModel: selectedModelGeneration,
    setSelectedModel: setSelectedModelGeneration,
    selectedModelParameters: selectedModelParametersGeneration,
    setSelectedModelParameters: setSelectedModelParametersGeneration,
  } = useActionsCapabilitiesStore();

  const { setCancelRequest, resetCancelRequest, cancelRequest } =
    useCancelRequestStore();
  const currentLocation = useLocation();

  // Create a ref to store the current cancel token source
  const cancelTokenSourceRef = useRef(null);

  // Set the cancel function in the store when component mounts
  useEffect(() => {
    setCancelRequest(() => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Request cancelled by user');

        // Dismiss any loading toasts
        toast.dismiss('video-generation-loading');
        toast.dismiss('video-edit-loading');
      }
    });

    // Cleanup function
    return () => {
      resetCancelRequest();
    };
  }, []);

  // Cancel request when changing pages or chats
  useEffect(() => {
    // Check if we're not in the chat page
    if (!currentLocation.pathname.includes('/chat')) {
      cancelRequest?.();
      return;
    }

    // Get current chat hash from URL
    const currentChatHash = parseQueryString(currentLocation.search)?.chat;

    // If we have a selected chat, check if it's different from the URL
    if (
      selectedChat?.chat?._id &&
      currentChatHash &&
      selectedChat.chat._id !== currentChatHash
    ) {
      cancelRequest?.();
    }
  }, [
    currentLocation.pathname,
    currentLocation.search,
    selectedChat?.chat?._id,
  ]);

  const handleGenerateResponse = async (prompt) => {
    const processMessage = visionInput(prompt);

    if (
      !validatePrompt(prompt) ||
      !validateRequiredParameters(
        selectedModelGeneration,
        selectedModelParametersGeneration
      ) ||
      !validateVideoGeneration(imagesPreview, selectedModelGeneration)
    ) {
      return;
    }

    // Create a new cancel token source for this request
    cancelTokenSourceRef.current = axios.CancelToken.source();
    setCancelRequest(() => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Request cancelled by user');
        // Dismiss any loading toasts
        toast.dismiss('video-generation-loading');
      }
    });

    setLoading(true);
    setInputValue('');
    setImagesPreview([]);
    const hash = parseQueryString(window.location.search)?.chat;

    const userInput = {
      user: store.profile._id,
      data: {
        role: 'user',
        content: prompt,
        basePrompt: prompt,
      },
      date: new Date(),
    };

    addMessage(userInput);
    setChat({ ...selectedChat?.chat });
    setAutoScroll(true);

    const modelType = selectedModelGeneration.type.toLowerCase();
    // Show loading toast for video generation
    const loadingToast =
      modelType === 'video'
        ? toast.loading(
            'Please wait, your video is being generated. This could take a couple of minutes',
            {
              id: 'video-generation-loading',
            }
          )
        : null;

    try {
      setStreamingMessage(
        createStreamingMessage(
          selectedModelGeneration.abbreviation,
          selectedModelGeneration._id,
          modelType,
          selectedModelParametersGeneration?.num_images
        )
      );

      const { attachment, ...configWithoutAttachments } =
        selectedModelParametersGeneration;

      const response = await store.api.post(
        '/v2/ai/generate',
        {
          hash,
          message: processMessage,
          model: selectedModelGeneration._id,
          type: selectedModelGeneration.type,
          attachment: attachment
            ? processMessage.find((item) => item.type === 'image_url')
            : null,
          ...configWithoutAttachments,
        },
        {
          cancelToken: cancelTokenSourceRef.current.token,
        }
      );

      if (!hash) {
        const { hash: newHash } = response.data.data;
        const params = new URLSearchParams(location.search);
        params.append('chat', newHash);
        navigate(
          { pathname: location.pathname, search: params.toString() },
          { shallow: true }
        );
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        // Dismiss any loading toasts
        toast.dismiss('video-generation-loading');
      } else {
        const errorMessage =
          error.response?.data?.error ??
          'Something went wrong, please try again.';
        smartToastCriticalError(errorMessage, {
          id: 'api-error-generate',
          duration: TOAST_DURATIONS.IMPORTANT,
        });
      }
    } finally {
      // Dismiss loading toast if it exists
      if (modelType === 'video') {
        toast.dismiss(loadingToast);
      }
      await store.refreshTokenAndProfileCoins();
      const currentHash = parseQueryString(window.location.search)?.chat;
      if (currentHash) {
        await getChat(currentHash);
      } else {
        onNewChat();
      }
      isProcessing.current = false;
      resetState({
        setLoading,
        setInputValue,
        setImagesPreview,
        setSelectedModelGeneration,
        setSelectedModelParametersGeneration,
        setGenerationsModelsActive,
        setStreamingMessage,
      });
      // Reset cancel token after request is complete
      cancelTokenSourceRef.current = null;
    }
  };

  const onRegenerateResponseGenerator = async (message) => {
    setLoading(true);
    setAutoScroll(true);
    const hash = parseQueryString(window.location.search)?.chat;

    try {
      // Create a new cancel token source for this request
      cancelTokenSourceRef.current = axios.CancelToken.source();

      const modelType = message?.model_type;
      setStreamingMessage(
        createStreamingMessage(
          message?.model,
          message?.model_ref,
          modelType,
          message?.num_images
        )
      );

      await store.api.post(
        '/v2/ai/regenerate',
        { hash, message: message._id },
        {
          cancelToken: cancelTokenSourceRef.current.token,
        }
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        // Dismiss any loading toasts
        toast.dismiss('video-generation-loading');
      } else {
        const errorMessage =
          error.response?.data?.error ??
          'Something went wrong, please try again.';
        smartToastCriticalError(errorMessage, {
          id: 'api-error-regenerate',
        });
      }
    } finally {
      const currentHash = parseQueryString(window.location.search)?.chat;
      if (currentHash) {
        await getChat(currentHash);
      }
      resetState({
        setLoading,
        setInputValue,
        setImagesPreview,
        setSelectedModelGeneration,
        setSelectedModelParametersGeneration,
        setGenerationsModelsActive,
        setStreamingMessage,
      });
    }
  };

  const onEditMediaGeneration = async (
    editText,
    datePrompt,
    currentEditChat,
    imagesEdit,
    countWordsEdit,
    previousChat,
    undoOptimization
  ) => {
    setLoading(true);
    setAutoScroll(true);
    const hash = parseQueryString(window.location.search)?.chat;
    const lastMessage = Array.isArray(previousChat.messages[0])
      ? previousChat.messages[0][0]
      : previousChat.messages[0];

    try {
      // Create a new cancel token source for this request
      cancelTokenSourceRef.current = axios.CancelToken.source();

      const modelType = lastMessage.model_type;
      // Show loading toast for video generation
      const loadingToast =
        modelType === 'video'
          ? toast.loading(
              'Please wait, your video is being edited. This could take a couple of minutes',
              {
                id: 'video-edit-loading',
              }
            )
          : null;

      // Create streaming message
      setStreamingMessage(
        createStreamingMessage(
          lastMessage.model,
          lastMessage.modelRef,
          modelType,
          lastMessage.num_images
        )
      );

      // Update chat messages
      const selectedMessageIndex = currentEditChat.messages.findIndex(
        (message) => message.date === datePrompt
      );

      if (selectedMessageIndex !== -1) {
        currentEditChat.messages =
          currentEditChat.messages.slice(selectedMessageIndex);

        // Update the first message with new content
        if (Array.isArray(currentEditChat.messages[0].data.content)) {
          currentEditChat.messages[0].data.content = [];
          currentEditChat.messages[0].data.content.push(...imagesEdit);
          currentEditChat.messages[0].data.content.push({
            type: 'text',
            text: editText,
          });
        } else {
          currentEditChat.messages[0].data.content = editText;
        }

        currentEditChat.messages[0].date = new Date();
        setChat({ ...currentEditChat });
        setMessages([...currentEditChat.messages]);
      }

      // Make API call
      const { prompt, ...configWithoutPrompt } =
        lastMessage?.additional_config || {};
      const response = await store.api.post(
        '/v2/ai/edit',
        {
          hash,
          question: lastMessage.question,
          message: visionInput(editText),
          model: lastMessage.modelRef,
          type: lastMessage.model_type,
          ...configWithoutPrompt,
        },
        {
          cancelToken: cancelTokenSourceRef.current.token,
        }
      );

      if (!response.data.success) {
        if (
          response.data.error === 'no_coins' ||
          response.data.error === 'missing_coins'
        ) {
          toastReward({
            title: 'Missing coins',
            message: 'Recharge your account on the profile page',
            linkText: "It's quick and easy. Click here!",
            linkUrl: '/my-profile/pricing',
            image: '/gifs/coin.gif',
            time: 10000,
          });
        } else {
          smartToastCriticalError(response.data.error, {
            id: 'api-error-edit',
          });
        }
      } else {
        const currentChat = selectedChat;
        const obj = {
          ...currentChat?.chat,
          _id: response.data.hash,
          title: response.data.title,
        };
        setChat(obj);

        if (!hash) {
          const { hash: newHash } = response.data.data;
          const params = new URLSearchParams(location.search);
          params.append('chat', newHash);
          navigate(
            { pathname: location.pathname, search: params.toString() },
            { shallow: true }
          );
        }
      }

      // Dismiss loading toast if it exists
      if (modelType === 'video') {
        toast.dismiss(loadingToast);
      }

      await store.refreshTokenAndProfileCoins();
      const currentHash = parseQueryString(window.location.search)?.chat;
      if (currentHash) {
        await getChat(currentHash);
      } else {
        onNewChat();
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        // Dismiss any loading toasts
        toast.dismiss('video-edit-loading');
      } else if (error?.response?.data?.error?.message) {
        toast(error?.response?.data?.error?.message, {
          icon: '⚠️',
          duration: TOAST_DURATIONS.WARNING,
          id: 'warning-edit',
        });
      }
      console.error(error);
    } finally {
      setLoading(false);
      setStreamingMessage(null);
    }
  };

  return {
    handleGenerateResponse,
    onRegenerateResponseGenerator,
    onEditMediaGeneration,
  };
};

export default useMediaGeneratorServices;
